<template>
  <div class="relative">
    <div
      v-if="showRawMessages"
      class="mb-2"
    >
      <div class="rounded-lg bg-gray-900 p-4">
        <VueJsonPretty
          :data="message"
          :deep="2"
          :show-double-quotes="false"
          :show-length="true"
          class="!bg-transparent"
          :showLine="false"
          :collapsed-on-click-brackets="true"
          :path-selectable="true"
        />
      </div>
    </div>
    <div class="flex gap-x-2 sm:gap-x-4">
      <div class="w-full max-w-[90%] grow space-y-3 md:max-w-2xl">
        <div
          class="space-y-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-900"
        >
          <div
            class="prose prose-sm max-w-none text-sm text-gray-800 dark:prose-invert prose-p:my-1 prose-pre:border
              prose-pre:border-neutral-200 prose-pre:bg-neutral-100 prose-ol:my-0.5 prose-ol:list-decimal
              prose-ol:pl-5 prose-ul:my-0.5 prose-ul:list-disc prose-ul:pl-5 prose-li:my-0.5 prose-li:pl-2
              prose-li:marker:text-gray-400 dark:text-white dark:prose-pre:border-neutral-700
              dark:prose-pre:bg-neutral-800 dark:prose-li:marker:text-gray-500 [&_a:hover]:text-purple-700
              [&_a]:text-purple-600 [&_a]:no-underline"
            v-html="renderedContent"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, onMounted, inject } from 'vue'
  import type { AIChatSessionMessage } from '@/types/generated'
  import IconRenderer from './IconRenderer.vue'
  import VueJsonPretty from 'vue-json-pretty'
  import 'vue-json-pretty/lib/styles.css'
  import { createApp, h } from 'vue'
  import MarkdownIt from 'markdown-it'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: {
      Page: 'Page',
      Pages: 'Pages'
    },
    de: {
      Page: 'Seite',
      Pages: 'Seiten'
    }
  })

  const props = defineProps<{
    message: AIChatSessionMessage
  }>()

  const debugPanelRef = inject('debugPanelRef')
  const showRawMessages = computed(() => debugPanelRef?.value?.showRawMessages || false)

  const md = new MarkdownIt({
    html: true,
    breaks: true,
    linkify: true
  })

  const entityRegistry = new Map()
  const documentRegistry = new Map()
  let registryCounter = 0

  const handleEntityClick = (entity) => {
    console.log('Entity clicked:', entity)
    window.dispatchEvent(new CustomEvent('entity-click', { detail: entity }))
  }

  const handleDocumentClick = (document) => {
    console.log('Document clicked:', document)
    window.dispatchEvent(new CustomEvent('document-click', { detail: document }))
  }

  const renderIcon = (type: 'user' | 'building' | 'book') => {
    const div = document.createElement('div')
    const app = createApp(IconRenderer, { type })
    app.mount(div)
    return div.innerHTML
  }

  const getEntityHtml = (entity) => {
    const iconHtml = renderIcon(entity.is_company ? 'building' : 'user')
    const key = `entity-${registryCounter++}`
    entityRegistry.set(key, entity)
    return `<span class="entity-link inline-flex cursor-pointer items-center rounded border border-purple-100 bg-purple-50/50 px-1.5 py-0.5 text-[11px] font-medium text-gray-800 dark:text-white" data-ref="${key}">${iconHtml}${entity.name}</span>`
  }

  const getDocumentHtml = (doc) => {
    const iconHtml = renderIcon('book')
    const key = `doc-${registryCounter++}`
    documentRegistry.set(key, doc)
    const pageText =
      doc.pages.length === 1
        ? `${t('Page')} ${doc.pages[0]}`
        : `${t('Pages')}: ${doc.pages.join(', ')}`
    return `<span class="document-link inline-flex cursor-pointer items-center gap-x-1 rounded border border-purple-100 bg-purple-50/50 px-1.5 py-0.5 text-[11px] font-medium text-gray-800 dark:text-white" data-ref="${key}">
      ${iconHtml}${doc.title}
      <span class="text-gray-500">(${pageText})</span>
    </span>`
  }

  const renderedContent = computed(() => {
    // Clear registries before each render
    entityRegistry.clear()
    documentRegistry.clear()
    registryCounter = 0

    if (!props.message.content?.chunks) return ''

    // Build content with HTML for entities and documents
    const content = props.message.content.chunks.reduce((acc, chunk) => {
      if (chunk.type === 'text') {
        return acc + chunk.text
      } else if (chunk.type === 'entity') {
        return acc + getEntityHtml(chunk)
      } else if (chunk.type === 'document') {
        return acc + getDocumentHtml(chunk)
      }
      return acc
    }, '')

    // Render markdown
    return md.render(content)
  })

  onMounted(() => {
    const container = document.querySelector('.prose')
    if (!container) return

    container.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      const entityLink = target.closest('.entity-link')
      const documentLink = target.closest('.document-link')

      if (entityLink) {
        const ref = entityLink.getAttribute('data-ref')
        if (ref) {
          const entity = entityRegistry.get(ref)
          if (entity) {
            handleEntityClick(entity)
          }
        }
      }

      if (documentLink) {
        const ref = documentLink.getAttribute('data-ref')
        if (ref) {
          const document = documentRegistry.get(ref)
          if (document) {
            handleDocumentClick(document)
          }
        }
      }
    })
  })
</script>

<style>
  .prose :deep(p) {
    margin: 0;
    display: inline;
  }

  .prose :deep(ul),
  .prose :deep(ol) {
    margin: 0;
    padding-left: 1.5em;
  }

  .prose :deep(li) {
    margin: 0;
  }

  .prose :deep(li:not(:last-child)) {
    margin-bottom: 0.25em;
  }
</style>

<style scoped>
  :deep(.vjs-tree) {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
      'Courier New', monospace !important;
    font-size: 0.75rem !important;
    line-height: 1rem !important;
    background-color: transparent !important;
    color: rgb(229 231 235) !important;
  }

  :deep(.vjs-tree .vjs-value__string) {
    color: rgb(229 231 235) !important;
  }

  :deep(.vjs-tree .vjs-value__number) {
    color: rgb(129 140 248) !important;
  }

  :deep(.vjs-tree .vjs-value__boolean) {
    color: rgb(244 114 182) !important;
  }

  :deep(.vjs-tree .vjs-value__null) {
    color: rgb(248 113 113) !important;
  }

  :deep(.vjs-tree .vjs-key) {
    color: rgb(139 92 246) !important;
  }
</style>
